import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Loader2, Send, CheckCircle2, AlertTriangle, X, Plus,
} from "lucide-react";

const DEFAULT = {
  enabled: false,
  recipients: [],
  day_of_week: 0,
  hour_utc: 8,
  last_sent_at: null,
  last_sent_status: null,
  last_sent_error: null,
};

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const formatRelative = (iso) => {
  if (!iso) return "Never sent";
  try {
    const when = new Date(iso);
    return `Last run: ${when.toLocaleString()}`;
  } catch {
    return `Last run: ${iso}`;
  }
};

const WeeklyDigestCard = () => {
  const { demoMode, getAccessToken } = useAuth();
  const [state, setState] = useState(DEFAULT);
  const [newRecipient, setNewRecipient] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const getHeaders = async () => {
    if (demoMode) return {};
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.get(`${API}/settings/weekly-digest`, { headers });
      setState({ ...DEFAULT, ...res.data });
    } catch (e) {
      console.error("weekly-digest load failed", e);
      toast.error("Could not load weekly digest settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const addRecipient = () => {
    const val = newRecipient.trim();
    if (!val) return;
    if (!val.includes("@")) {
      toast.error("That doesn't look like a valid email address");
      return;
    }
    if (state.recipients.includes(val)) {
      toast.error("Recipient already added");
      return;
    }
    setState((s) => ({ ...s, recipients: [...s.recipients, val] }));
    setNewRecipient("");
  };

  const removeRecipient = (addr) => {
    setState((s) => ({ ...s, recipients: s.recipients.filter((r) => r !== addr) }));
  };

  const save = async () => {
    if (state.enabled && state.recipients.length === 0) {
      toast.error("Add at least one recipient before enabling the digest");
      return;
    }
    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await axios.put(
        `${API}/settings/weekly-digest`,
        {
          enabled: state.enabled,
          recipients: state.recipients,
          day_of_week: state.day_of_week,
          hour_utc: state.hour_utc,
        },
        { headers },
      );
      setState((s) => ({ ...s, ...res.data }));
      toast.success("Weekly digest settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (state.recipients.length === 0) {
      toast.error("Add at least one recipient first");
      return;
    }
    setSendingTest(true);
    try {
      const headers = await getHeaders();
      const res = await axios.post(
        `${API}/settings/weekly-digest/send-test`,
        {},
        { headers },
      );
      if (res.data.success) {
        toast.success(`Digest sent to ${res.data.sent} recipient(s)`);
      } else {
        toast.error(
          `Failed to send: ${(res.data.errors || []).join(", ") || "check backend email config"}`,
        );
      }
      // Refresh last_sent fields
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test digest");
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="weekly-digest-card-loading">
        <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading weekly digest…
        </CardContent>
      </Card>
    );
  }

  const toggleEnabled = () => setState((s) => ({ ...s, enabled: !s.enabled }));
  const hourLabel = (h) => `${String(h).padStart(2, "0")}:00 UTC`;

  return (
    <Card data-testid="weekly-digest-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Mail className="h-5 w-5" />
          Weekly Digest Email
          {state.enabled && (
            <Badge variant="success" className="ml-2 bg-green-100 text-green-800">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Enabled
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Opt-in weekly summary email covering apps packaged, tested, deployed,
          success rate, and auto-updates. Off by default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <Label className="text-base font-medium">Send weekly digest</Label>
            <p className="text-sm text-muted-foreground mt-1">
              {formatRelative(state.last_sent_at)}
              {state.last_sent_status && (
                <span className="ml-2 text-xs">· status: {state.last_sent_status}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleEnabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              state.enabled ? "bg-green-500" : "bg-slate-400"
            }`}
            data-testid="weekly-digest-toggle"
            aria-pressed={state.enabled}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                state.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Recipients */}
        <div className="space-y-2">
          <Label>Recipients</Label>
          <p className="text-xs text-muted-foreground">
            Add every address that should receive the Monday-morning summary —
            e.g. individual tenant admins or a shared distribution list.
          </p>
          <div className="flex gap-2">
            <Input
              type="email"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
              placeholder="person@company.com"
              data-testid="weekly-digest-new-recipient"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addRecipient}
              data-testid="weekly-digest-add-recipient-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2" data-testid="weekly-digest-recipient-list">
            {state.recipients.length === 0 && (
              <span className="text-sm text-muted-foreground italic">
                No recipients yet.
              </span>
            )}
            {state.recipients.map((addr) => (
              <span
                key={addr}
                className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-900 rounded-full text-sm"
                data-testid={`weekly-digest-recipient-${addr}`}
              >
                {addr}
                <button
                  type="button"
                  onClick={() => removeRecipient(addr)}
                  className="hover:text-blue-700"
                  aria-label={`Remove ${addr}`}
                  data-testid={`weekly-digest-remove-${addr}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Day</Label>
            <Select
              value={String(state.day_of_week)}
              onValueChange={(v) => setState((s) => ({ ...s, day_of_week: Number(v) }))}
            >
              <SelectTrigger data-testid="weekly-digest-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAY_NAMES.map((d, i) => (
                  <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Time (UTC)</Label>
            <Select
              value={String(state.hour_utc)}
              onValueChange={(v) => setState((s) => ({ ...s, hour_utc: Number(v) }))}
            >
              <SelectTrigger data-testid="weekly-digest-hour-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, h) => (
                  <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {state.last_sent_status && state.last_sent_error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Last send had an issue</AlertTitle>
            <AlertDescription className="text-xs">{state.last_sent_error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={save}
            disabled={saving}
            className="flex-1"
            data-testid="weekly-digest-save-btn"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
            ) : (
              "Save digest settings"
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={sendTest}
            disabled={sendingTest || state.recipients.length === 0}
            data-testid="weekly-digest-send-test-btn"
          >
            {sendingTest ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" /> Send test digest now</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeeklyDigestCard;
